import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  message,
  Upload,
  DatePicker,
  InputNumber,
  Select,
  Checkbox,
  Spin,
  Alert,
} from 'antd'
import {
  FormOutlined,
  CheckCircleOutlined,
  CameraOutlined,
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { env } from '../config/environment'
import type { UploadFile } from 'antd/es/upload/interface'
import { 
  getCustomerProfile, 
  storeCustomerProfile, 
  clearExpiredProfiles,
  clearCustomerProfile
} from '../utils/customerStorage'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select

interface FormField {
  id: string
  type: string
  label: string
  name: string
  required?: boolean
  placeholder?: string
  options?: string[]
  validation?: {
    min?: number
    max?: number
    pattern?: string
  }
}

interface CustomerForm {
  id: string
  name: string
  description?: string
  formFields: FormField[]
  settings?: {
    enableImageUpload?: boolean
  }
}

export const FormComplete: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { customer, portalConfig, login, logout, isAuthenticated } = useCustomerAuth()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState<CustomerForm | null>(null)
  const [photoFiles, setPhotoFiles] = useState<UploadFile[]>([])
  const [submitted, setSubmitted] = useState(false)
  const [autoAuthAttempted, setAutoAuthAttempted] = useState(false)
  const [autoAuthLoading, setAutoAuthLoading] = useState(true)
  const [checkingSubmission, setCheckingSubmission] = useState(false)
  const [submissionCheckAttempted, setSubmissionCheckAttempted] = useState(false)

  // Clear expired profiles on mount
  useEffect(() => {
    clearExpiredProfiles()
  }, [])

  // Check for stored profile and auto-authenticate on mount (before loading form)
  useEffect(() => {
    const attemptAutoAuth = async () => {
      if (!token || autoAuthAttempted) {
        setAutoAuthLoading(false)
        return
      }

      // If already authenticated, skip auto-auth
      if (isAuthenticated) {
        setAutoAuthLoading(false)
        setAutoAuthAttempted(true)
        return
      }

      try {
        // First, check for form-specific stored profile
        const storedProfile = getCustomerProfile(token)
        
        if (storedProfile && storedProfile.token) {
          console.log('🔐 Found stored customer profile for form, attempting auto-authentication...')
          try {
            await login(storedProfile.token)
            console.log('✅ Auto-authentication successful')
            message.success('Welcome back!')
          } catch (error) {
            console.error('❌ Auto-authentication failed:', error)
            // Clear invalid stored profile
            if (token) {
              clearCustomerProfile(token)
            }
          }
        } else {
          console.log('ℹ️ No stored customer profile found for form:', token)
          
          // Check if there's a general customerToken in localStorage
          const generalToken = localStorage.getItem('customerToken')
          if (generalToken) {
            console.log('🔐 Found general customerToken in localStorage, attempting validation...')
            try {
              // Try to login with the general token
              await login(generalToken)
              console.log('✅ General token authentication successful')
              message.success('Welcome back!')
            } catch (error) {
              console.error('❌ General token authentication failed:', error)
              // Token might be expired, clear it
              localStorage.removeItem('customerToken')
            }
          }
        }
      } catch (error) {
        console.error('Error during auto-authentication:', error)
      } finally {
        setAutoAuthAttempted(true)
        setAutoAuthLoading(false)
      }
    }

    attemptAutoAuth()
  }, [token, autoAuthAttempted, isAuthenticated, login])

  // Check if form was already submitted and redirect to portal if authenticated
  useEffect(() => {
    const checkSubmissionAndRedirect = async () => {
      // Only check if authenticated, auto-auth is complete, and we haven't already checked
      if (!isAuthenticated || autoAuthLoading || !customer?.id || !token || checkingSubmission || submissionCheckAttempted) {
        return
      }

      setCheckingSubmission(true)
      setSubmissionCheckAttempted(true)
      try {
        // Check if form was already submitted by this customer
        const response = await fetch(
          `${env.apiBaseUrl}/customer-forms/public/${token}/check-submission`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('customerToken')}`,
            },
          }
        )

        if (response.ok) {
          const result = await response.json()
          if (result.alreadySubmitted) {
            console.log('✅ Form already submitted, redirecting to portal...')
            message.info('You have already submitted this form. Redirecting to your portal...')
            const defaultLandingPage = portalConfig?.defaultLandingPage || '/dashboard'
            setTimeout(() => {
              navigate(defaultLandingPage, { replace: true })
            }, 1000)
            return
          }
        } else if (response.status === 404) {
          // Customer not found or deleted - clear stored profile and logout
          const errorData = await response.json().catch(() => ({}))
          if (errorData.error === 'Customer not found') {
            console.warn('⚠️ Customer account not found, clearing stored profile...')
            if (token) {
              clearCustomerProfile(token)
            }
            // Clear general token as well
            localStorage.removeItem('customerToken')
            // Logout from context
            logout()
            message.warning('Your account is no longer available. Please contact support if you believe this is an error.')
          }
          // Continue to show form
        } else if (response.status === 401 || response.status === 500) {
          // Authentication failed or server error - clear auth and show form
          const errorData = await response.json().catch(() => ({}))
          console.warn('⚠️ Authentication or server error checking submission:', response.status, errorData)
          
          // If it's an auth error, clear the token
          if (response.status === 401) {
            if (token) {
              clearCustomerProfile(token)
            }
            localStorage.removeItem('customerToken')
            logout()
          }
          
          // Don't redirect - just show the form
          // The user can still fill out the form even if they're not authenticated
        } else {
          // Other errors - log but don't retry
          console.warn('⚠️ Could not check form submission status:', response.status)
          // Continue to show form if check fails
        }
      } catch (error) {
        console.error('Error checking form submission:', error)
        // Continue to show form if check fails - don't retry
      } finally {
        setCheckingSubmission(false)
      }
    }

    checkSubmissionAndRedirect()
  }, [isAuthenticated, autoAuthLoading, customer, token, portalConfig, navigate, logout])

  useEffect(() => {
    // Only load form after auto-auth attempt is complete
    if (!autoAuthLoading) {
      loadForm()
    }
  }, [token, autoAuthLoading])

  const loadForm = async () => {
    try {
      setLoading(true)
      const response = await fetch(
        `${env.apiBaseUrl}/customer-forms/public/${token}`
      )
      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to load form')
      }
      
      setFormData(result.data)
    } catch (error: any) {
      message.error(error.message || 'Failed to load form')
    } finally {
      setLoading(false)
    }
  }

  const handlePhotoUpload = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async () => {
        try {
          if (!customer?.id) {
            throw new Error('Customer not authenticated. Please refresh the page.')
          }

          const base64 = (reader.result as string).split(',')[1]
          
          const response = await fetch(
            `${env.apiBaseUrl}/files/upload`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('customerToken')}`,
              },
              body: JSON.stringify({
                base64,
                filename: file.name,
                mimeType: file.type,
                category: 'customer_photos',
                size: file.size,
                relatedType: 'customer',
                relatedId: customer.id,
              }),
            }
          )
          
          const result = await response.json()
          
          if (!response.ok) {
            throw new Error(result.message || 'Upload failed')
          }
          
          console.log('Photo uploaded successfully:', result.id, 'for customer:', customer.id)
          resolve(result.id)
        } catch (error) {
          console.error('Photo upload error:', error)
          reject(error)
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true)

      if (!customer?.id) {
        message.error('Customer not authenticated. Please refresh the page.')
        return
      }

      // Upload photos first
      const photoIds: string[] = []
      console.log('📸 Photo files to upload:', photoFiles.length, photoFiles)
      
      for (const file of photoFiles) {
        // Ant Design Upload file can be either the file itself or have originFileObj
        const fileToUpload = (file as any).originFileObj || file
        console.log('📸 Processing file:', fileToUpload.name, 'Type:', typeof fileToUpload)
        
        // Check if it's actually a File object
        if (fileToUpload instanceof File) {
          try {
            const photoId = await handlePhotoUpload(fileToUpload)
            photoIds.push(photoId)
            console.log('✅ Photo uploaded with ID:', photoId)
          } catch (error: any) {
            console.error('❌ Photo upload failed:', error)
            message.error(`Failed to upload ${fileToUpload.name}: ${error.message}`)
          }
        } else {
          console.warn('⚠️ File is not a File object:', fileToUpload)
        }
      }

      console.log(`📸 Uploaded ${photoIds.length} photos, submitting form with photo IDs:`, photoIds)

      // Submit form
      const response = await fetch(
        `${env.apiBaseUrl}/customer-forms/public/${token}/submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('customerToken')}`,
          },
          body: JSON.stringify({
            formData: values,
            photos: photoIds,
          }),
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Form submission failed')
      }

      // Store customer profile for 30-day auto-authentication
      if (customer && token) {
        const customerToken = localStorage.getItem('customerToken')
        if (customerToken) {
          storeCustomerProfile(token, {
            id: customer.id,
            firstName: customer.firstName,
            lastName: customer.lastName,
            email: customer.email,
            phone: customer.phone,
          }, customerToken)
          console.log('✅ Customer profile stored for 30-day auto-authentication')
        }
      }

      setSubmitted(true)
      message.success('Form submitted successfully!')
      
      // Redirect to configured landing page after 2 seconds
      const defaultLandingPage = portalConfig?.defaultLandingPage || '/dashboard'
      setTimeout(() => {
        navigate(defaultLandingPage)
      }, 2000)

    } catch (error: any) {
      console.error('Form submission error:', error)
      message.error(error.message || 'Failed to submit form')
    } finally {
      setSubmitting(false)
    }
  }

  const renderField = (field: FormField) => {
    const rules: any[] = []
    if (field.required) {
      rules.push({ required: true, message: `${field.label} is required` })
    }

    switch (field.type) {
      case 'firstName':
      case 'lastName':
      case 'email':
      case 'phone':
      case 'address':
      case 'city':
      case 'state':
      case 'zipCode':
      case 'company':
      case 'text':
        return (
          <Form.Item
            key={field.id}
            name={field.name}
            label={field.label}
            rules={rules}
          >
            <Input placeholder={field.placeholder} size="large" />
          </Form.Item>
        )

      case 'textarea':
        return (
          <Form.Item
            key={field.id}
            name={field.name}
            label={field.label}
            rules={rules}
          >
            <TextArea rows={4} placeholder={field.placeholder} style={{ fontSize: '16px' }} />
          </Form.Item>
        )

      case 'number':
        return (
          <Form.Item
            key={field.id}
            name={field.name}
            label={field.label}
            rules={rules}
          >
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              min={field.validation?.min}
              max={field.validation?.max}
              placeholder={field.placeholder}
            />
          </Form.Item>
        )

      case 'date':
        return (
          <Form.Item
            key={field.id}
            name={field.name}
            label={field.label}
            rules={rules}
          >
            <DatePicker style={{ width: '100%' }} size="large" />
          </Form.Item>
        )

      case 'dropdown':
        return (
          <Form.Item
            key={field.id}
            name={field.name}
            label={field.label}
            rules={rules}
          >
            <Select placeholder={field.placeholder} size="large">
              {field.options?.map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )

      case 'checkbox':
        return (
          <Form.Item
            key={field.id}
            name={field.name}
            valuePropName="checked"
            rules={rules}
          >
            <Checkbox>{field.label}</Checkbox>
          </Form.Item>
        )

      default:
        return null
    }
  }

  // Show loading while attempting auto-auth, checking submission, or loading form
  if (autoAuthLoading || checkingSubmission || loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '16px'
      }}>
        <Spin size="large" />
      </div>
    )
  }

  if (submitted) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '16px'
      }}>
        <Card style={{ 
          maxWidth: 500, 
          width: '100%',
          textAlign: 'center',
          borderRadius: '12px'
        }}>
          <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
          <Title level={3}>Form Submitted Successfully!</Title>
          <Text type="secondary">Thank you for completing the form. Redirecting to dashboard...</Text>
        </Card>
      </div>
    )
  }

  if (!formData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        padding: '16px'
      }}>
        <Alert
          message="Form Not Found"
          description="The requested form could not be loaded."
          type="error"
          style={{ maxWidth: 500, width: '100%' }}
        />
      </div>
    )
  }

  return (
    <div style={{ 
      padding: '16px', 
      maxWidth: 800, 
      margin: '0 auto',
      minHeight: '100vh'
    }}>
      <Card style={{ borderRadius: '12px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2} style={{ fontSize: '24px', marginBottom: 8 }}>
              <FormOutlined style={{ marginRight: 8 }} />
              {formData.name}
            </Title>
            {formData.description && (
              <Text type="secondary" style={{ fontSize: '14px' }}>{formData.description}</Text>
            )}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={customer ? {
              firstName: customer.firstName,
              lastName: customer.lastName,
              email: customer.email,
              phone: customer.phone,
            } : {}}
          >
            {formData.formFields.map((field) => renderField(field))}

            {formData.settings?.enableImageUpload !== false && (
            <Form.Item label="Upload Photos">
              <Upload
                listType="picture-card"
                fileList={photoFiles}
                beforeUpload={(file) => {
                  // Create a proper UploadFile object with the file
                  const uploadFile: UploadFile = {
                    uid: file.uid || `${Date.now()}-${Math.random()}`,
                    name: file.name,
                    status: 'done',
                    originFileObj: file,
                  }
                  setPhotoFiles((prev) => [...prev, uploadFile])
                  console.log('📸 File added to list:', file.name, uploadFile)
                  return false // Prevent auto upload
                }}
                onRemove={(file) => {
                  setPhotoFiles((prev) => prev.filter(f => f.uid !== file.uid))
                }}
                accept="image/*"
              >
                {photoFiles.length < 10 && (
                  <div>
                    <CameraOutlined />
                    <div style={{ marginTop: 8, fontSize: '12px' }}>Upload</div>
                  </div>
                )}
              </Upload>
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: '12px' }}>
                You can upload up to 10 photos
              </Text>
            </Form.Item>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                size="large"
                block
                style={{ height: '48px' }}
              >
                Submit Form
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  )
}

